import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Typography, Paper } from '@mui/material';
import { reportError } from '../utils/report_error';

interface ErrorBoundaryProps {
    children: ReactNode;
    // Current route (path + query + hash). When it changes, the boundary clears its error so
    // navigating to another page — including query-string-only navigation — recovers without a
    // full reload.
    routeKey?: string;
}
interface ErrorBoundaryState {
    hasError: boolean;
    // The route that was active when the crash was caught. The reset in componentDidUpdate keys
    // off this so it fires only after the user navigates AWAY from the crashing route — not when
    // the navigation that caused the crash is itself the routeKey change.
    errorKey?: string;
}

// App-level render error boundary. A crash in any routed page degrades to this fallback —
// the app shell and nav stay mounted — instead of unmounting the whole React tree to a
// blank white screen, and every caught error is forwarded to the monitoring hook.
//
// Error boundaries must be class components: there is no hook equivalent for
// getDerivedStateFromError / componentDidCatch.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        // Record the crash route so componentDidUpdate can tell "navigated off the fallback" apart
        // from "this navigation IS what crashed." Log the full routeKey (path+query+hash), not just
        // pathname, so telemetry keeps the query that often identifies the failing state.
        this.setState({ errorKey: this.props.routeKey });
        reportError(error, { componentStack: info.componentStack, route: this.props.routeKey ?? window.location.pathname });
    }

    componentDidUpdate(): void {
        // Recover once the user navigates to a route different from where the crash happened.
        // Guarding on errorKey (set in componentDidCatch) prevents a spurious reset -> re-render ->
        // re-report of the SAME error when a good->bad navigation is itself the routeKey change:
        // at that first update errorKey is either still unset or already equals the current route.
        if (this.state.hasError && this.state.errorKey !== undefined && this.props.routeKey !== this.state.errorKey) {
            this.setState({ hasError: false, errorKey: undefined });
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
                    <Paper elevation={2} style={{ maxWidth: '34rem', width: '100%', padding: '2.5rem', textAlign: 'center' }}>
                        <Typography variant='h5' style={{ marginBottom: '0.75rem' }}>Something went wrong on this page.</Typography>
                        <Typography variant='body1' color='textSecondary' style={{ marginBottom: '1.75rem' }}>
                            The error has been logged. Choose another page from the menu, or reload to try again.
                        </Typography>
                        <Button variant='contained' color='primary' onClick={() => window.location.reload()}>Reload page</Button>
                    </Paper>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
